import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { PriceTrendMini } from "@/components/PriceTrendMini";

const RingMetric = ({ label, value }) => (
  <div className="flex flex-col items-center">
    <div className="relative w-16 h-16">
      <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F5F2EB" strokeWidth="3.5" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#C85A32" strokeWidth="3.5" strokeDasharray={`${value}, 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono font-bold text-sm text-[var(--teak)]">{value}</div>
    </div>
    <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mt-2">{label}</div>
  </div>
);

export const Neighborhoods = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/neighborhoods").then(({ data }) => setList(data)); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="mb-10">
        <span className="section-eyebrow">Hyperlocal Intel</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--teak)] mt-2">Mysuru Neighborhood Insights</h1>
        <p className="text-[var(--ink-muted)] mt-3 max-w-2xl">
          Walkability, transit, and green scores for every pocket — so you choose a street, not just a city.
        </p>
      </div>

      <div className="space-y-5">
        {list.map((n, i) => (
          <div key={n.name} className="p-6 lg:p-8 rounded-3xl bg-white border border-[var(--line)] grid lg:grid-cols-12 gap-6 fade-in-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`neighborhood-${n.name}`}>
            <div className="lg:col-span-5">
              <h2 className="font-serif text-3xl text-[var(--teak)]">{n.name}</h2>
              <p className="italic text-[var(--primary)] mt-1">{n.tagline}</p>
              <p className="text-sm text-[var(--ink-muted)] mt-3 leading-relaxed">{n.vibe}</p>
              <div className="mt-5 font-mono text-sm">
                <span className="text-[var(--ink-muted)]">Price: </span>
                <span className="text-[var(--teak)] font-bold">₹{n.avg_sqft_min.toLocaleString("en-IN")} – ₹{n.avg_sqft_max.toLocaleString("en-IN")}/sq.ft</span>
              </div>
              <div className="mt-2 flex gap-2 flex-wrap">
                {n.highlights.map((h) => <span key={h} className="chip">{h}</span>)}
              </div>
            </div>
            <div className="lg:col-span-4 flex items-center justify-center gap-8">
              <RingMetric label="Walk" value={n.walk_score} />
              <RingMetric label="Transit" value={n.transit_score} />
              <RingMetric label="Green" value={n.green_score} />
            </div>
            <div className="lg:col-span-3 flex flex-col justify-center gap-2">
              <div className="font-mono text-3xl text-[var(--teak)]">{n.property_count}</div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">Active listings</div>
              <div className="mt-2">
                <PriceTrendMini locality={n.name} />
              </div>
              <Link to={`/listings?locality=${encodeURIComponent(n.name)}`} className="btn-ghost mt-2 justify-start" data-testid={`view-${n.name}-btn`}>
                View listings <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
